import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef } from "react";
import { createPortal } from "react-dom";
const ModalStackContext = /*#__PURE__*/ createContext(null);
let nextModalId = 0;
/**
 * Provider that tracks the modal stack for proper visual layering.
 *
 * When modals are nested:
 * - A single shared backdrop overlays everything except the topmost modal
 * - Parent modals are marked as "receded" (scaled down, visually behind)
 *
 * WHY refs + forceUpdate (instead of useState): React 18 StrictMode runs mount
 * effects twice (mount → cleanup → mount). With useState-based modal tracking,
 * each step commits separately and consumers briefly see modals.length=1 between
 * register/unregister/register, which makes the parent picker briefly un-recede
 * and corrupts CSS transitions. With a ref store, all three operations mutate
 * the same array synchronously and the batched re-render lands on the final
 * state — no intermediate flicker.
 */ export function ModalStackProvider({ children }) {
    const modalsRef = useRef([]);
    const nextLevelRef = useRef(1);
    const [, forceUpdate] = useReducer((n)=>n + 1, 0);
    const register = useCallback(()=>{
        const id = nextModalId++;
        const level = nextLevelRef.current++;
        modalsRef.current = [
            ...modalsRef.current,
            {
                id,
                level
            }
        ];
        forceUpdate();
        return {
            id,
            level
        };
    }, []);
    const unregister = useCallback((id)=>{
        modalsRef.current = modalsRef.current.filter((m)=>m.id !== id);
        nextLevelRef.current = modalsRef.current.length > 0 ? Math.max(...modalsRef.current.map((m)=>m.level)) + 1 : 1;
        forceUpdate();
    }, []);
    const modals = modalsRef.current;
    const depth = modals.length;
    const topLevel = depth === 0 ? 0 : Math.max(...modals.map((m)=>m.level));
    const isReceded = useCallback((level)=>modals.some((m)=>m.level > level), [
        modals
    ]);
    const value = useMemo(()=>({
            register,
            unregister,
            depth,
            isReceded,
            topLevel
        }), [
        register,
        unregister,
        depth,
        isReceded,
        topLevel
    ]);
    // Safety net for a Radix teardown race: when stacked dismissable layers (e.g.
    // a form Dialog plus its unsaved-changes confirm, or a modal popover inside a
    // dialog) unmount together, react-dismissable-layer can skip restoring
    // `body { pointer-events }` — its restore is gated on a shared layer counter —
    // leaving the whole page unclickable until refresh. Once the stack is empty,
    // nothing should be holding pointer-events off, so clear any stuck lock.
    //
    // Read the live ref count, not the captured `depth`: a child Dialog registers
    // in its own (earlier-running) effect, so on the open render this effect can
    // still see `depth === 0` even though a modal just opened — clearing on the
    // captured value would clobber the lock Radix legitimately set.
    useEffect(function clearStuckPointerEventsLock() {
        if (modalsRef.current.length === 0 && document.body.style.pointerEvents === "none") {
            document.body.style.pointerEvents = "";
        }
    }, [
        depth
    ]);
    // Shared backdrop z-index: sits just below the topmost modal content
    // - First modal content: z-101
    // - Shared backdrop (when 2nd modal opens): z-110 (above first modal z-101)
    // - Second modal content: z-111
    // - Popovers/dropdowns: z-200 (always accessible)
    const backdropZIndex = 100 + (topLevel - 1) * 10;
    return /*#__PURE__*/ _jsxs(ModalStackContext.Provider, {
        value: value,
        children: [
            children,
            depth >= 1 && /*#__PURE__*/ createPortal(/*#__PURE__*/ _jsx("div", {
                className: "fixed inset-0 z-(--z-modal) bg-dialog-overlay backdrop-blur-xs animate-in fade-in-0 duration-200",
                "aria-hidden": "true"
            }), document.body),
            depth > 1 && /*#__PURE__*/ createPortal(/*#__PURE__*/ _jsx("div", {
                className: "fixed inset-0 bg-black/50 backdrop-blur-xs animate-in fade-in-0 duration-200",
                style: {
                    zIndex: backdropZIndex
                },
                "aria-hidden": "true"
            }), document.body)
        ]
    });
}
/**
 * Hook to access the modal stack context.
 * Returns null if used outside of ModalStackProvider (graceful degradation).
 */ export function useModalStack() {
    return useContext(ModalStackContext);
}

//# sourceMappingURL=ModalStackProvider.js.map